import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';

type PushPermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

const DISMISS_KEY = 'onesignal_prompt_dismissed_at';
const DISMISS_COOLDOWN_MS = 1000 * 60 * 60 * 24 * 7; // 7 días

declare global {
  interface Window {
    OneSignalDeferred?: Array<(OneSignal: any) => void>;
  }
}

/** Runs a callback against the OneSignal SDK once it is ready. */
const withOneSignal = (cb: (OneSignal: any) => void | Promise<void>) => {
  if (typeof window === 'undefined') return;
  window.OneSignalDeferred = window.OneSignalDeferred || [];
  window.OneSignalDeferred.push(async (OneSignal: any) => {
    try {
      await cb(OneSignal);
    } catch (err) {
      console.error('[OneSignal] operation failed', err);
    }
  });
};

/**
 * Push notification state backed by OneSignal. Requesting permission through
 * this hook also registers the OneSignal subscription (and links the signed-in
 * user via external_id), which a raw Notification.requestPermission() call
 * would not do.
 */
export const usePushNotifications = () => {
  const { user } = useAuth();
  const [permission, setPermission] = useState<PushPermissionState>('default');
  const [isSupported, setIsSupported] = useState(false);
  const [optedIn, setOptedIn] = useState(false);

  useEffect(() => {
    const supported =
      typeof window !== 'undefined' &&
      'Notification' in window &&
      'serviceWorker' in navigator;

    setIsSupported(supported);
    if (!supported) {
      setPermission('unsupported');
      return;
    }

    setPermission(Notification.permission as PushPermissionState);

    let cleanup: (() => void) | undefined;

    withOneSignal((OneSignal) => {
      const sync = () => {
        setPermission(
          (OneSignal.Notifications?.permission
            ? 'granted'
            : (Notification.permission as PushPermissionState)) ?? 'default'
        );
        setOptedIn(Boolean(OneSignal.User?.PushSubscription?.optedIn));
      };

      sync();

      const onChange = () => sync();
      OneSignal.User?.PushSubscription?.addEventListener?.('change', onChange);
      OneSignal.Notifications?.addEventListener?.('permissionChange', onChange);

      cleanup = () => {
        OneSignal.User?.PushSubscription?.removeEventListener?.('change', onChange);
        OneSignal.Notifications?.removeEventListener?.('permissionChange', onChange);
      };
    });

    return () => cleanup?.();
  }, []);

  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!isSupported) return false;

    withOneSignal(async (OneSignal) => {
      await OneSignal.Notifications.requestPermission();
      if (OneSignal.User?.PushSubscription?.optIn) {
        await OneSignal.User.PushSubscription.optIn();
      }
      if (user?.id) {
        await OneSignal.login(user.id);
      }
      setPermission(Notification.permission as PushPermissionState);
      setOptedIn(Boolean(OneSignal.User?.PushSubscription?.optedIn));
    });

    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    return true;
  }, [isSupported, user?.id]);

  const unsubscribe = useCallback(async () => {
    withOneSignal(async (OneSignal) => {
      await OneSignal.User?.PushSubscription?.optOut?.();
      setOptedIn(false);
    });
  }, []);

  const dismissPrompt = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  }, []);

  const shouldShowPrompt = useCallback(() => {
    if (!isSupported || permission !== 'default') return false;

    const dismissed = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissed) return Date.now() - dismissed > DISMISS_COOLDOWN_MS;
    return true;
  }, [isSupported, permission]);

  return {
    permission,
    isSupported,
    isSubscribed: permission === 'granted' && optedIn,
    isBlocked: permission === 'denied',
    subscribe,
    unsubscribe,
    // Backwards-compatible alias
    requestPermission: subscribe,
    dismissPrompt,
    shouldShowPrompt,
  };
};
